import {createContext, use, useMemo} from 'react'
import type {AgentProfile} from '../agents/agent-profile'
import {DEFAULT_PROFILE} from '../agents/agent-profile'
import {resolveAgentProfile} from '../agents/resolve-agent-profile'

const AgentProfileContext = createContext<AgentProfile>(DEFAULT_PROFILE)

interface AgentProfileProviderProps {
  /** The agent slug used to resolve the profile. */
  slug?: string
  children: React.ReactNode
}

/**
 * Provides a resolved AgentProfile to the component tree.
 *
 * Resolves the profile by looking up the slug in the profile registry.
 * Place this provider above components that need agent-type-aware behavior
 * (SessionHeader, SessionLogs, TaskChat input area, etc.).
 */
export function AgentProfileProvider({slug, children}: AgentProfileProviderProps) {
  const profile = useMemo(() => resolveAgentProfile(slug), [slug])

  return <AgentProfileContext value={profile}>{children}</AgentProfileContext>
}

/**
 * Returns the current agent profile from context.
 * Falls back to DEFAULT_PROFILE if no provider is present (safe for gradual adoption).
 */
export function useAgentProfile(): AgentProfile {
  return use(AgentProfileContext)
}
